import { Alert, Snackbar } from '@mui/material';
import { Stack } from '@mui/system';
import { nanoid } from 'nanoid';
import React, { useCallback, useContext, useMemo, useState } from 'react';

type MessageType = 'error' | 'warning' | 'info' | 'success';

export interface ISnackbarState {
  notifications: Notification[];
}

export interface Notification {
  message: string;
  type: MessageType;
}

export type SnackbarContextValue = {
  notifications: { message: string; type: MessageType }[];
  set: (notifications: Notification[]) => void;
  clear: () => void;
};

export const SnackbarContext = React.createContext<SnackbarContextValue | undefined>(undefined);

export function useSnackbar() {
  const context = useContext(SnackbarContext);

  if (!context) {
    throw new Error("You can't use snackbar outside snackbar provider");
  }

  return context;
}

export const SnackbarProvider: React.FC<{
  children: React.ReactElement;
}> = ({ children }) => {
  const [state, setState] = useState<ISnackbarState>({
    notifications: [],
  });

  const set = useCallback(
    (notifications: Notification[]) => {
      setState({
        notifications,
      });
    },
    [setState],
  );

  const clear = useCallback(() => {
    setState({ notifications: [] });
  }, [setState]);

  const context = useMemo(() => {
    return {
      ...state,
      set,
      clear,
    };
  }, [state, set, clear]);

  return (
    <SnackbarContext.Provider value={context}>
      {children}
      <Stack>
        {state.notifications.map(notification => (
          <Snackbar
            key={nanoid()}
            open={state.notifications.length > 0}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={clear} severity={notification.type}>
              {notification.message}
            </Alert>
          </Snackbar>
        ))}
      </Stack>
    </SnackbarContext.Provider>
  );
};
